import { DOCUMENT, effect, inject, Injectable, untracked } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "./auth.service";
import { userContextStore } from "./user-context.store";
import { footerLegalLinksStore } from "../../domains/coremedia/navigation/data/footer-legal-links.store";
import { navigationStore } from "../../domains/coremedia/navigation/data/navigation.store";

/**
 * Wires locale changes (country / language) to:
 *  1. ngx-translate + <html lang> (UI language)
 *  2. Navigation (re)load (so X-Country-Code / X-Language headers are updated)
 *  3. Footer legal-links (re)load (so X-Language / X-Fallback-Language headers are updated)
 *
 * Navigation and footer links are only loaded once the user is authenticated: the gateway
 * answers every unauthenticated request to the CoreMedia API with 401. The stores therefore do
 * not load on init; this service performs the initial load as soon as the session exists.
 *
 * Must be injected at the application root (in App component) to activate.
 */
@Injectable({ providedIn: "root" })
export class LocaleEffectsService {
  private readonly store = inject(userContextStore);
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);
  private readonly navStore = inject(navigationStore);
  private readonly footerLinksStore = inject(footerLegalLinksStore);

  constructor() {
    // Sync UI language whenever the stored language changes
    effect(() => {
      const language = this.store.language();
      this.translate.use(language);
      this.document.documentElement.lang = language;
    });

    // Load navigation and footer legal links once authenticated and the user context is ready,
    // and reload them whenever country or language change afterwards.
    effect(() => {
      this.store.country();
      this.store.language();

      if (this.auth.isAuthenticated() && this.store.status() === "ready") {
        untracked(() => {
          this.navStore.load();
          this.footerLinksStore.load();
        });
      }
    });
  }
}
